import { Check } from 'lucide-react';
import {
  VMS_SERVICE_JOURNEY_STAGES,
  type VmsServiceJourneyStage,
  type VmsServiceJourneyStepUi,
} from '../../pages/vms/vmsServiceJourneyModel';

function JourneyConnector({ afterDone }: { afterDone: boolean }) {
  return (
    <span
      className={`mx-0.5 hidden h-0.5 w-4 shrink-0 sm:inline-block ${
        afterDone ? 'bg-emerald-400' : 'bg-gray-300'
      }`}
      aria-hidden
    />
  );
}

function JourneyStepPill({
  label,
  ui,
}: {
  label: string;
  ui: VmsServiceJourneyStepUi;
}) {
  if (ui === 'done') {
    return (
      <span
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 sm:px-3 sm:text-xs"
        title={`${label} — completed`}
      >
        <span
          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-600"
          aria-hidden
        >
          <Check className="h-2.5 w-2.5 text-white" strokeWidth={2.5} />
        </span>
        <span className="whitespace-nowrap">{label}</span>
      </span>
    );
  }

  if (ui === 'current') {
    return (
      <span
        className="inline-flex shrink-0 items-center rounded-full border border-dashed border-sky-400 bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-800 sm:px-3 sm:text-xs"
        title={`${label} — in progress`}
      >
        <span className="whitespace-nowrap">{label}</span>
      </span>
    );
  }

  return (
    <span
      className="inline-flex shrink-0 items-center rounded-full border border-gray-200 bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-500 sm:px-3 sm:text-xs"
      title={`${label} — not started`}
    >
      <span className="whitespace-nowrap">{label}</span>
    </span>
  );
}

export function VmsServiceJourneyStrip({
  stepUi,
}: {
  stepUi: Record<VmsServiceJourneyStage, VmsServiceJourneyStepUi>;
}) {
  return (
    <div
      className="vms-service-journey-strip flex min-w-0 items-center gap-0.5 overflow-x-auto py-0.5 sm:gap-1"
      role="list"
      aria-label="Service journey"
    >
      {VMS_SERVICE_JOURNEY_STAGES.map((stage, index) => {
        const ui = stepUi[stage.key];
        const prevUi = index > 0 ? stepUi[VMS_SERVICE_JOURNEY_STAGES[index - 1]!.key] : null;
        return (
          <div key={stage.key} className="flex shrink-0 items-center" role="listitem">
            {index > 0 ? <JourneyConnector afterDone={prevUi === 'done'} /> : null}
            <JourneyStepPill label={stage.label} ui={ui} />
          </div>
        );
      })}
    </div>
  );
}
